import React, { useState, useEffect } from 'react';
import './HeadlineSelectionModal.css';

const HeadlineSelectionModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  headlines = [], 
  usedHeadlines = [], 
  scriptInfo = {},
  loading = false 
}) => {
  const [selectedHeadline, setSelectedHeadline] = useState('');
  const [useCustomHeadline, setUseCustomHeadline] = useState(false);
  const [customHeadline, setCustomHeadline] = useState('');

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedHeadline('');
      setUseCustomHeadline(false);
      setCustomHeadline('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = () => {
    const headlineToSubmit = useCustomHeadline ? customHeadline.trim() : selectedHeadline;
    if (headlineToSubmit && onSelect) {
      onSelect(headlineToSubmit);
    }
  };

  const isValidSelection = useCustomHeadline 
    ? customHeadline.trim().length > 0 && customHeadline.trim().length <= 60
    : selectedHeadline.length > 0;

  const getHeadlineStatus = (headline) => {
    const isUsed = usedHeadlines.includes(headline);
    return {
      available: !isUsed,
      disabled: isUsed,
      className: isUsed ? 'headline-disabled' : 'headline-available'
    };
  };

  return (
    <div className="headline-modal-overlay" onClick={onClose}>
      <div className="headline-modal" onClick={e => e.stopPropagation()}>
        <div className="headline-modal-header">
          <h3>Select Thumbnail Headline</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="headline-modal-content">
          <div className="script-info">
            <h4>Script: {scriptInfo.type || 'Unknown'}</h4>
            <p className="script-preview">
              {scriptInfo.content ? scriptInfo.content.substring(0, 100) + '...' : 'No preview available'}
            </p>
          </div>

          <div className="headline-section">
            <div className="headline-toggle">
              <button 
                className={`toggle-btn ${!useCustomHeadline ? 'active' : ''}`}
                onClick={() => {
                  setUseCustomHeadline(false);
                  setCustomHeadline('');
                }}
              >
                Generated Headlines
              </button>
              <button 
                className={`toggle-btn ${useCustomHeadline ? 'active' : ''}`}
                onClick={() => {
                  setUseCustomHeadline(true);
                  setSelectedHeadline('');
                }}
              >
                Custom Headline
              </button>
            </div>

            {!useCustomHeadline ? (
              <div className="generated-headlines">
                <h4>Choose a Generated Headline:</h4>
                <div className="headlines-list">
                  {headlines.map((headline, index) => {
                    const status = getHeadlineStatus(headline);
                    return (
                      <div 
                        key={index}
                        className={`headline-option ${status.className} ${selectedHeadline === headline ? 'selected' : ''}`}
                        onClick={() => {
                          if (status.available) {
                            setSelectedHeadline(headline);
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="headline"
                          value={headline}
                          checked={selectedHeadline === headline}
                          disabled={status.disabled}
                          onChange={() => {
                            if (status.available) {
                              setSelectedHeadline(headline);
                            }
                          }}
                        />
                        <span className="headline-text">
                          {headline}
                          {status.disabled && <span className="used-indicator"> (Already Used)</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="custom-headline">
                <h4>Write Your Custom Headline:</h4>
                <div className="custom-headline-input-container">
                  <textarea
                    className="custom-headline-input"
                    placeholder="Enter your custom headline (max 60 characters)..."
                    value={customHeadline}
                    onChange={(e) => setCustomHeadline(e.target.value)}
                    maxLength={60}
                    rows={2}
                  />
                  <div className="character-count">
                    <span className={customHeadline.length > 50 ? 'warning' : ''}>
                      {customHeadline.length}/60 characters
                    </span>
                  </div>
                </div>
                <div className="custom-headline-tips">
                  <p><strong>Tips for effective headlines:</strong></p>
                  <ul>
                    <li>Keep it short and punchy (5-8 words ideal)</li>
                    <li>Use strong action verbs (transforms, reveals, discovers)</li>
                    <li>Include numbers when relevant</li>
                    <li>Create curiosity without being clickbait</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="headline-modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSelect}
            disabled={!isValidSelection || loading}
          >
            {loading ? 'Submitting...' : 'Submit for Production'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeadlineSelectionModal;
